import React from "react";

const krw = (n) => `₩${Math.round(Number(n) || 0).toLocaleString()}`;

const BtcIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4 text-warning" aria-hidden="true">
        <path d="M4 19V9" /><path d="M10 19V5" /><path d="M16 19v-8" /><path d="M20 19H3" />
    </svg>
);

const DeltaIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-brand" aria-hidden="true">
        <path d="M3 8h13l-3-3" /><path d="M21 16H8l3 3" />
    </svg>
);

const StockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-gain" aria-hidden="true">
        <path d="M3 17l6-6 4 4 7-7" /><path d="M14 8h6v6" />
    </svg>
);

const signedPct = (pct) => (pct == null ? "—" : `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`);

/**
 * StrategyOverviewCards — 전체 뷰의 전략 3분할 카드 (DAS-175 Option A).
 *
 * 세 전략은 봐야 할 숫자가 서로 다르다 — 적립 2종(BTC·해외주식)은 원금 대비 성과,
 * 델타전략은 리밸런싱. 카드 헤더(이름·배지·값)만 공통으로 두고 하단 한 줄은 전략별로 다르게 쓴다.
 * 카드를 누르면 해당 전략 뷰로 전환한다.
 */
class StrategyOverviewCards extends React.Component {
    renderCard({ key, icon, name, badge, badgeClass, value, sub, subClass, footLeft, footRight, footRightClass, onClick }) {
        return (
            <button
                key={key}
                type="button"
                onClick={onClick}
                className="flex min-h-[44px] w-full flex-col gap-3 rounded-2xl border border-surface-border bg-surface-card p-4 text-left shadow-card transition-colors hover:bg-surface-hover"
            >
                <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-content">
                            {icon}
                            {name}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass}`}>{badge}</span>
                    </div>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true">
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </div>
                <div className="min-w-0">
                    <div className="truncate font-num text-xl font-bold text-content sm:text-2xl">{value}</div>
                    <div className={`mt-1 truncate font-num text-xs ${subClass}`}>{sub}</div>
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-surface-border pt-2.5 text-xs">
                    <span className="truncate text-content-muted">{footLeft}</span>
                    <span className={`shrink-0 font-num ${footRightClass || "text-content-secondary"}`}>{footRight}</span>
                </div>
            </button>
        );
    }

    render() {
        const { btcSummary, delta, stockSummary, onSelect } = this.props;

        const btc = btcSummary || {};
        const btcPnlColor = (btc.pnl || 0) >= 0 ? "text-gain" : "text-loss";

        const stockValue = Number(stockSummary?.total_value_krw);
        const hasStock = Number.isFinite(stockValue) && stockValue > 0;
        const stockPnlPct = Number(stockSummary?.pnl_pct);
        const hasStockPnlPct = Number.isFinite(stockPnlPct);
        const stockPrincipal = Number(stockSummary?.principal_krw) || 0;
        const stockAccounts = Array.isArray(stockSummary?.accounts) ? stockSummary.accounts : [];

        // 카드는 보유 평가액 내림차순으로 배치한다 (DAS-175) — 큰 돈이 위/왼쪽에 온다.
        // 세그먼트 탭 순서는 고정이다: 값에 따라 탭이 움직이면 누르던 자리가 매번 바뀐다.
        // sort는 안정 정렬이라 값이 같으면(예: 셋 다 0) 아래 선언 순서가 그대로 유지된다.
        const cardConfigs = [
            {
                sortValue: btc.hasHolding ? Number(btc.value) || 0 : 0,
                key: "btc",
                icon: <BtcIcon />,
                name: "BTC 적립",
                badge: "DCA",
                badgeClass: "bg-gain-subtle text-gain",
                value: btc.hasHolding ? krw(btc.value) : "—",
                sub: btc.hasHolding
                    ? `${(btc.pnl || 0) >= 0 ? "+" : "-"}${krw(Math.abs(btc.pnl || 0))} (${signedPct(btc.pnlPct)}) · 원금 ${krw(btc.principal)}`
                    : "보유 BTC 현물 없음",
                subClass: btc.hasHolding ? `${btcPnlColor} font-semibold` : "text-content-muted",
                footLeft: "업비트 · 현물",
                footRight: btc.hasHolding
                    ? `${Number(btc.qty).toLocaleString(undefined, { maximumFractionDigits: 8 })} BTC`
                    : "—",
                onClick: () => onSelect("btc"),
            },
            {
                sortValue: Number(delta?.base) || 0,
                key: "delta",
                icon: <DeltaIcon />,
                name: "델타전략",
                badge: "헤지",
                badgeClass: "bg-warning-subtle text-warning",
                value: krw(delta?.base),
                sub: `Fiat ${krw(delta?.deltaFiatBalance)} · Risky ${krw(delta?.riskyBalance)}`,
                subClass: "text-content-muted",
                footLeft: "보내야 할 금액",
                footRight: `${krw(delta?.sendBalance)} → ${delta?.receiveExchange || "-"}`,
                footRightClass: "font-semibold text-warning",
                onClick: () => onSelect("delta"),
            },
            {
                sortValue: hasStock ? stockValue : 0,
                key: "stock",
                icon: <StockIcon />,
                name: "해외주식 적립",
                badge: "DCA",
                badgeClass: "bg-gain-subtle text-gain",
                value: hasStock ? krw(stockValue) : "—",
                sub: hasStock
                    ? `${signedPct(hasStockPnlPct ? stockPnlPct : null)} · 원금 ${krw(stockPrincipal)}`
                    : "등록된 적립 없음",
                subClass: hasStock
                    ? hasStockPnlPct
                        ? `${stockPnlPct >= 0 ? "text-gain" : "text-loss"} font-semibold`
                        : "text-content-muted"
                    : "text-content-muted",
                footLeft: stockAccounts.length > 0 ? `계좌 ${stockAccounts.length}` : "계좌 미등록",
                footRight: hasStock
                    ? `$${Number(stockSummary?.total_value_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                    : "—",
                onClick: () => onSelect("stock"),
            },
        ];

        const cards = cardConfigs
            .slice()
            .sort((a, b) => b.sortValue - a.sortValue)
            .map((config) => this.renderCard(config));

        return <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{cards}</div>;
    }
}

export default StrategyOverviewCards;
